import { digger } from '@cbre/digger';
import {
  areaUomAbbrev,
  areaUomAbbrevCountry,
  concatValuesBy,
  defaultTxt,
  EMPTY_STRING,
  isDash,
  simpleValueValidation,
  UserSettings,
} from '@miq/utils';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocaleId as _LocaleId } from '../../../../../models/locale.model';
import { ReportRecord } from '../../../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { MapHandlerOptions } from '../../../../../models/windward-blueprint.model';
import { sortAttributesFields } from '../../../utils/argument-sorting';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { currencyLov, PropertyType, propertyTypeDisplayFnFactory, valueWithUomPrefix } from '../../us/us.common';
import { getCertificates } from '../certificates';
import {
  addLocationToArgument,
  BLANK_SPACE,
  disclaimerText,
  displayImageWrapper,
  FLOOR_LOADING_UOM_TONS,
  formatNumberByMIQLocale,
  getAvailabilityValueByLocale,
  getMeasurement,
  getPropertyNotes,
  getTransportType,
  PROPERTY_MEASUREMENT_TYPES,
} from '../common';
import { getPropertyNameRoute } from '../common-display-fn';
import { distanceUomAbbrev } from '../transport-functions';

const measurementSpaceID = 3875;

export const displayFnMap: TemplateFieldsDisplayFn = {
  98: {
    // 98 - Primary Image
    displayFn: displayImageWrapper,
  },
  266: {
    // 266 - Summary Map
    displayFn: (_, options): Observable<any[]> => {
      const { reportId } = options;
      const edpRecordId = undefined;
      const mapOptions: MapHandlerOptions = {
        enableCache: true,
        isGroupByProperty: true,
        width: 1200,
        height: 810,
      };
      return of([reportId, edpRecordId, mapOptions]);
    },
  },
  852: {
    // 852 - Property Name
    displayFn: (record: ReportRecord, _, propertyName): string | string[] => {
      if (propertyName == null) {
        return BLANK_SPACE;
      }
      return mapAndFormatDynamicValue(record, propertyName, undefined, undefined, undefined, BLANK_SPACE);
    },
  },
  853: {
    // 853 - Custom Address
    displayFn: (record: ReportRecord, options, ...args): string => {
      if (args == null || !args.length) {
        return BLANK_SPACE;
      }
      const { displayFnArgumentsOrder } = options;
      const finalArgs = sortAttributesFields(args, displayFnArgumentsOrder);
      const values = finalArgs.map(arg =>
        mapAndFormatDynamicValue(record, arg, undefined, undefined, undefined, BLANK_SPACE)
      );
      return concatValuesBy(', ', ...values);
    },
    routeFn: getPropertyNameRoute,
  },
  854: {
    // 854 - Selected Properties Label
    displayFn: displayFnWrapper,
  },
  855: {
    // 855 Key Figures Header
    displayFn: displayFnWrapper,
  },
  856: {
    // 856 - Status
    displayFn: (record: ReportRecord, _, status): Observable<string> => {
      if (status == null) {
        return of(BLANK_SPACE);
      }
      return status.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const lovKeyValue = mapAndFormatDynamicValue(record, status, lovs);
          const lovValue = lovs.find(a => a.key === lovKeyValue);
          return lovValue ? lovValue.value : BLANK_SPACE;
        })
      );
    },
  },
  857: {
    // 857 - Rent Per Year
    displayFn: (record: ReportRecord, options, minAskingRateYearly, maxAskingRateYearly): Observable<string> => {
      if (minAskingRateYearly == null || maxAskingRateYearly == null) {
        return of(BLANK_SPACE);
      }
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          /*
           * prepare and add extra currencies as we currently do not have
           * swedish or norwegian currency symbols.
           */
          const extraCurrencyValues: LovValues[] = [
            { key: 'Swedish Krona', value: 'SEK' },
            { key: 'Norwegian Krone', value: 'NOK' },
          ];
          currencyLOV.push(...extraCurrencyValues);

          /*
           * adding location attribute to the arguments so the angular
           * formatNumber function, inside the valueWithUomPrefix function,
           * uses it to correctly format the number
           */
          const minArgument = addLocationToArgument(minAskingRateYearly, options.locale);
          const maxArgument = addLocationToArgument(maxAskingRateYearly, options.locale);

          // retrieve formatted values
          const minRent = valueWithUomPrefix(record, PropertyType.Industrial, minArgument, currencyLOV);
          const maxRent = valueWithUomPrefix(record, PropertyType.Industrial, maxArgument, currencyLOV);
          const minValue = Array.isArray(minRent) ? minRent[0] : minRent;
          const maxValue = Array.isArray(maxRent) ? maxRent[0] : maxRent;

          // validate if there is any empty value we should omit from the result
          const values: string[] = [];
          if (simpleValueValidation(minValue) && !isDash(minValue)) {
            values.push(minValue);
          }
          if (simpleValueValidation(maxValue) && !isDash(maxValue)) {
            values.push(maxValue);
          }

          // return blank space if empty as per user requirements
          return values.map(v => v?.trim()).join(' - ') || BLANK_SPACE;
        })
      );
    },
  },
  858: {
    // 858 - Space Available
    displayFn: (record: ReportRecord, options, spaceAvailable): any => {
      if (
        spaceAvailable == null ||
        spaceAvailable.uomArgument == null ||
        spaceAvailable.uomArgument.lovValues == null
      ) {
        return BLANK_SPACE;
      }
      return spaceAvailable.uomArgument.lovValues.pipe(
        map((uomLOV: LovValues[]) => {
          /*
           * adding location attribute to the argument so the angular
           * formatNumber function, inside the getIndustrialValue function,
           * uses it to correctly format the number
           */
          const spaceAvailArgWithLocation = addLocationToArgument(spaceAvailable, options.locale);

          // generate custom function, adding the uomLOV array
          function customDisplayFn(_innerRecord, _innerOptions, innerArg) {
            return mapAndFormatDynamicValue(record, innerArg, uomLOV);
          }
          // generate and execute function to retrieve value only from industrial usage
          const getIndustrialValue = propertyTypeDisplayFnFactory(PropertyType.Industrial, customDisplayFn);
          // retrieve industrial space available value
          const spaceValPerUsage = getIndustrialValue(record, undefined, spaceAvailArgWithLocation) as string[];

          // retrieve actual value from usages result. return empty if array is empty
          let spaceVal: string;
          if (Array.isArray(spaceValPerUsage)) {
            if (spaceValPerUsage.length === 0) {
              // return blank space if empty as per user requirements
              return BLANK_SPACE;
            }
            spaceVal = spaceValPerUsage[0];
          }

          // return blank space if empty, null, or dash as per user requirements
          return simpleValueValidation(spaceVal) && !isDash(spaceVal) ? spaceVal : BLANK_SPACE;
        })
      );
    },
  },
  859: {
    // 859 - Description
    displayFn: (record: ReportRecord, options): string => {
      return getPropertyNotes(record.data, options.locale, 'Property Description for Reports');
    },
  },
  860: {
    // 860 - Location
    displayFn: (record: ReportRecord, options): string => {
      return getPropertyNotes(record.data, options.locale, 'Location Description');
    },
  },
  861: {
    // 861 - Map Link
    displayFn: (record: ReportRecord): string => {
      const MAP_LINK_URL = 'https://www.google.com/maps/search/?api=1&query=';
      const latitude = digger(record, 'data.postal_address.latitude');
      const longitude = digger(record, 'data.postal_address.longitude');
      if (latitude == null || longitude == null) {
        return BLANK_SPACE;
      }
      return encodeURI(`${MAP_LINK_URL}${latitude},${longitude}`);
    },
  },
  862: {
    // 862 - Web Link
    displayFn: (record: ReportRecord): string => {
      const digitalPath = `digital[digital_type_desc='Web URL']`;
      const digitalAddress = `digital_address`;
      const digitalValue = digger(record.data, `${digitalPath}.${digitalAddress}`);
      const webLink = Array.isArray(digitalValue) ? digitalValue[digitalValue.length - 1] : digitalValue;
      return simpleValueValidation(webLink) ? webLink : BLANK_SPACE;
    },
  },
  863: {
    // 863 - Distances
    displayFn: (record: ReportRecord): any[] => {
      const AMENITIES_TRANSPORT_TYPE = 'Transportation';
      const transportAmenities = digger(record, 'data.property_amenity') || [];

      const mappedTransportAmenities: any[] = [];
      for (const amenity of transportAmenities) {
        const {
          amenity_type_desc,
          property_amenity_type_desc,
          amenity_distance_from_property,
          amenity_distance_from_property_uom,
          amenity_desc,
        } = amenity;
        if (amenity_type_desc === AMENITIES_TRANSPORT_TYPE) {
          const uomAbbrev = distanceUomAbbrev(amenity_distance_from_property_uom);
          const distance = amenity_distance_from_property != null ? amenity_distance_from_property : '';
          const uom = uomAbbrev || '';

          if (simpleValueValidation(amenity_desc)) {
            const transportType = getTransportType(property_amenity_type_desc);
            const mappedAmenity = { transportType, value: `${amenity_desc} ${distance} ${uom}` };
            mappedTransportAmenities.push(mappedAmenity);
          }
        }
      }
      // show up to 5 values
      const max = mappedTransportAmenities.length < 5 ? mappedTransportAmenities.length : 5;
      return mappedTransportAmenities.slice(0, max);
    },
  },
  865: {
    // 865 - Total Size
    displayFn: (record: ReportRecord, options, nraArg): Observable<string> => {
      if (nraArg == null || nraArg.uomArgument == null || nraArg.uomArgument.lovValues == null) {
        return of(BLANK_SPACE);
      }
      return nraArg.uomArgument.lovValues.pipe(
        map((uomLOV: LovValues[]) => {
          const nra = 'net_rentable_area';
          const nraValue = digger(record.data, nra);
          const value = simpleValueValidation(nraValue)
            ? formatNumberByMIQLocale(nraValue, options.locale, nraArg.uomArgument.dataFormat)
            : BLANK_SPACE;
          const lovKeyValue = mapAndFormatDynamicValue(record, nraArg.uomArgument, uomLOV);
          const lovValue = uomLOV.find(a => a.key === lovKeyValue);
          return `${value} ${lovValue ? areaUomAbbrevCountry(lovValue.value, 'EMEA') : EMPTY_STRING}`;
        })
      );
    },
  },
  866: {
    // 866 - Available Space
    displayFn: (record: ReportRecord, options): string => {
      const industrialUsagesPath = `usages[ref_property_usage_type_desc='Industrial']`;
      const availableSpace = `total_available_surface_area`;
      const availableSpaceUom = `total_available_surface_area_uom_desc`;
      const availableDate = `date_property_available`;
      const spaceValue = digger(record.data, `${industrialUsagesPath}.${availableSpace}`);
      const spaceUomValue = digger(record.data, `${industrialUsagesPath}.${availableSpaceUom}`);
      const dateValue = digger(record.data, `${industrialUsagesPath}.${availableDate}`);
      const spaceUomAbbrev = areaUomAbbrev(spaceUomValue);
      const formattedSpaceVal =
        simpleValueValidation(spaceValue) && spaceValue !== defaultTxt
          ? formatNumberByMIQLocale(spaceValue, options.locale)
          : ' ';
      const availableSpaceValue = `${formattedSpaceVal} ${spaceUomAbbrev !== defaultTxt ? spaceUomAbbrev : ''}`;
      const dateDisplayFormat = UserSettings.getGlobalDateDisplayFormat('value');
      if (dateValue != null) {
        const date = new Date(dateValue);
        const availDate = moment(date, dateDisplayFormat);
        if (availDate.isValid() && moment().diff(availDate, 'days') < 0) {
          return `${availableSpaceValue} (${availDate.format(dateDisplayFormat)})`;
        }
        const availability = getAvailabilityValueByLocale(options.locale);
        return `${availableSpaceValue} (${availability})`;
      }
      return availableSpaceValue;
    },
  },
  868: {
    // 868 - Warehouse Space
    displayFn: (
      record: ReportRecord,
      options,
      rent: DisplayFnArgumentsInfo,
      measurementSpace: DisplayFnArgumentsInfo
    ): Observable<string> => {
      let actualMeasurementSpace = measurementSpace;
      let actualRent = rent;
      if (measurementSpace == null && rent.id === measurementSpaceID) {
        actualMeasurementSpace = rent;
        actualRent = null;
      }
      return getMeasurement(
        record,
        options,
        actualMeasurementSpace,
        actualRent,
        PROPERTY_MEASUREMENT_TYPES.warehouse,
        options.locale
      );
    },
  },
  869: {
    // 869 - Office Space
    displayFn: (
      record: ReportRecord,
      options,
      rent: DisplayFnArgumentsInfo,
      measurementSpace: DisplayFnArgumentsInfo
    ): Observable<string> => {
      let actualMeasurementSpace = measurementSpace;
      let actualRent = rent;
      if (measurementSpace == null && rent.id === measurementSpaceID) {
        actualMeasurementSpace = rent;
        actualRent = null;
      }
      return getMeasurement(
        record,
        options,
        actualMeasurementSpace,
        actualRent,
        PROPERTY_MEASUREMENT_TYPES.office,
        options.locale
      );
    },
  },
  870: {
    // 870 - Mezzanine Space
    displayFn: (
      record: ReportRecord,
      options,
      rent: DisplayFnArgumentsInfo,
      measurementSpace: DisplayFnArgumentsInfo
    ): Observable<string> => {
      let actualMeasurementSpace = measurementSpace;
      let actualRent = rent;
      if (measurementSpace == null && rent.id === measurementSpaceID) {
        actualMeasurementSpace = rent;
        actualRent = null;
      }
      return getMeasurement(
        record,
        options,
        actualMeasurementSpace,
        actualRent,
        PROPERTY_MEASUREMENT_TYPES.mezzanine,
        options.locale
      );
    },
  },
  871: {
    // 871 - Clear Height
    displayFn: (record: ReportRecord, options): string => {
      const industrialUsagesPath = `usages[ref_property_usage_type_desc='Industrial']`;
      const clearHeight = `maximum_clear_height`;
      const clearHeightUom = `maximum_clear_height_uom_desc`;
      const clearHeightFullPath = `${industrialUsagesPath}.${clearHeight}`;
      const clearHeightUomFullPath = `${industrialUsagesPath}.${clearHeightUom}`;
      const clearHeightValue = digger(record.data, clearHeightFullPath);
      const clearHeightUomValue = digger(record.data, clearHeightUomFullPath);
      const uomAbbrev = distanceUomAbbrev(clearHeightUomValue);
      const formattedCleatHeightVal = simpleValueValidation(clearHeightValue)
        ? formatNumberByMIQLocale(clearHeightValue, options.locale)
        : '';
      return `${formattedCleatHeightVal} ${simpleValueValidation(uomAbbrev) ? uomAbbrev : ''}`;
    },
  },
  872: {
    // 872 - Floor Loading
    displayFn: (record: ReportRecord, options, floorLoading) => {
      if (floorLoading == null || floorLoading.uomArgument == null) {
        return BLANK_SPACE;
      }
      return floorLoading.uomArgument.lovValues.pipe(
        map((lovs: LovValues[]) => {
          const floorLoadingPath = `floor_loading_capacity`;
          const floorLoadingUomPath = `floor_loading_capacity_uom`;
          const floorLoadingValue = digger(record.data, floorLoadingPath);
          const floorLoadingUomValue = digger(record.data, floorLoadingUomPath);
          const lovValue = lovs.find(a => a.key === floorLoadingUomValue);
          if (lovValue != null && lovValue.key != null && lovValue.key === 'Kg/Sq M') {
            const transformValue = !isNaN(floorLoadingValue)
              ? formatNumberByMIQLocale(floorLoadingValue / 1000, options.locale)
              : '';
            return `${transformValue} ${FLOOR_LOADING_UOM_TONS}`;
          }
          return `${simpleValueValidation(floorLoadingValue) ? floorLoadingValue : ''} ${
            lovValue ? lovValue.value : ''
          }`;
        })
      );
    },
  },
  873: {
    // 873 - Sprinkler System
    displayFn: (record: ReportRecord): string => {
      const sprinklerPath = `ref_property_sprinkler_type_desc`;
      const sprinklerValue = digger(record.data, sprinklerPath);
      return simpleValueValidation(sprinklerValue) ? sprinklerValue : BLANK_SPACE;
    },
  },
  874: {
    // 874 - Loading Bays
    displayFn: (record: ReportRecord): string => {
      const industrialUsagesPath = `usages[ref_property_usage_type_desc='Industrial']`;
      const bays = `number_dock_door`;
      const baysFullPath = `${industrialUsagesPath}.${bays}`;
      const loadingBaysValue = digger(record.data, baysFullPath);
      return simpleValueValidation(loadingBaysValue) ? loadingBaysValue : BLANK_SPACE;
    },
  },
  875: {
    // 875 - Certifications
    displayFn: getCertificates,
  },
  876: {
    // 876 - Floor Plan Image
    displayFn: displayImageWrapper,
  },
  877: {
    // 877 - Contacts Header
    displayFn: displayFnWrapper,
  },
  878: {
    // 878 - Disclaimer
    displayFn: (_, options): string => {
      if (options.locale == null) {
        return BLANK_SPACE;
      }
      return disclaimerText[options.locale] || BLANK_SPACE;
    },
  },
  890: {
    // 890 - Industrial & Logistics Header
    displayFn: displayFnWrapper,
  },
  891: {
    // 891 - Advisory & Transaction Header
    displayFn: displayFnWrapper,
  },
};
